import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit,
  CheckCircle,
  Block,
} from '@mui/icons-material';
import adminService from '../../services/adminService';

const MembershipManagement = () => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [expiredPlansCount, setExpiredPlansCount] = useState(0);
  const [expiringSoonCount, setExpiringSoonCount] = useState(0);

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      const response = await adminService.getMemberships({
        status: 'all',
        page: 1,
        limit: 500,
      });
      // Handle both old membership format and new subscription format
      const data = response.data.users || response.data.memberships || [];

      const usersWithSubscription = data.filter((user) => !!user.active_subscription?.plan_id);

      const now = new Date();
      const expiredCount = usersWithSubscription.filter((user) => {
        const status = (user.active_subscription?.status || '').toLowerCase();
        if (status === 'expired') {
          return true;
        }
        const endDate = user.active_subscription?.end_date
          ? new Date(user.active_subscription.end_date)
          : null;
        return !!endDate && endDate < now;
      }).length;

      const soonCount = usersWithSubscription.filter((user) => {
        const status = (user.active_subscription?.status || '').toLowerCase();
        if (status !== 'active') {
          return false;
        }
        const endDate = user.active_subscription?.end_date
          ? new Date(user.active_subscription.end_date)
          : null;
        if (!endDate) {
          return false;
        }

        const msDiff = endDate - now;
        const daysLeft = Math.ceil(msDiff / (1000 * 60 * 60 * 24));
        return daysLeft >= 0 && daysLeft <= 30;
      }).length;

      setExpiredPlansCount(expiredCount);
      setExpiringSoonCount(soonCount);

      // Only show users that currently have an active subscription.
      const activeSubscriptionUsers = usersWithSubscription.filter((user) => {
        const status = (user.active_subscription?.status || '').toLowerCase();
        return status === 'active';
      });
      
      // Transform subscription data to membership format for compatibility
      const transformedData = activeSubscriptionUsers.map(user => ({
        ...(function () {
          const endDate = user.active_subscription?.end_date
            ? new Date(user.active_subscription.end_date)
            : null;
          const rawStatus = (user.active_subscription?.status || 'inactive').toLowerCase();
          const nowDate = new Date();

          if (rawStatus === 'expired' || (endDate && endDate < nowDate)) {
            return { membershipStatusDisplay: 'expired' };
          }

          if (rawStatus === 'active' && endDate) {
            const daysLeft = Math.ceil((endDate - nowDate) / (1000 * 60 * 60 * 24));
            if (daysLeft >= 0 && daysLeft <= 30) {
              return { membershipStatusDisplay: 'expiring_soon' };
            }
          }

          return { membershipStatusDisplay: rawStatus };
        })(),
        id: user._id,
        name: user.name,
        email: user.email,
        membershipType: user.active_subscription?.plan_id?.name || 'Free',
        membershipExpiry: user.active_subscription?.end_date,
        membershipStatus: user.active_subscription?.status || 'inactive',
        daysRemaining: user.daysRemaining || 0,
        // Keep original user data
        ...user
      }));
      
      setMemberships(transformedData);
    } catch (error) {
      console.error('Error fetching memberships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMembershipUpdate = async (userId, data) => {
    try {
      await adminService.updateMembership(userId, data);
      fetchMemberships();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error updating membership:', error);
    }
  };

  const getMembershipColor = (type) => {
    // Handle both plan names and legacy membership types
    const lowerType = type?.toLowerCase() || '';
    
    if (lowerType.includes('diamond') || lowerType.includes('premium')) {
      return {
        bgcolor: '#1e3a8a',
        color: '#ffffff',
      };
    } else if (lowerType.includes('gold') || lowerType.includes('standard')) {
      return {
        bgcolor: '#0f766e',
        color: '#ffffff',
      };
    } else if (lowerType.includes('silver') || lowerType.includes('basic')) {
      return {
        bgcolor: '#475569',
        color: '#ffffff',
      };
    } else if (lowerType.includes('free')) {
      return {
        bgcolor: '#e2e8f0',
        color: '#334155',
      };
    } else {
      return {
        bgcolor: '#e2e8f0',
        color: '#334155',
      };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'expiring_soon':
        return 'warning';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = [
    { field: 'name', headerName: 'User Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'membershipType',
      headerName: 'Plan',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          sx={{
            ...getMembershipColor(params.value),
            fontWeight: 600,
          }}
          variant="filled"
          size="small"
        />
      ),
    },
    {
      field: 'membershipStatusDisplay',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'expiring_soon' ? 'expiring soon' : (params.value || 'inactive')}
          color={getStatusColor(params.value)}
          variant="filled"
          size="small"
        />
      ),
    },
    {
      field: 'membershipExpiry',
      headerName: 'Expiry Date',
      width: 150,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : 'N/A',
    },
    {
      field: 'daysRemaining',
      headerName: 'Days Left',
      width: 100,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          color={params.value <= 0 ? 'error' : params.value <= 30 ? 'warning' : 'textPrimary'}
        >
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'purchase_count',
      headerName: 'Times Purchased',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 0}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => {
              setSelectedUser(params.row);
              setOpenDialog(true);
            }}
          >
            <Edit />
          </IconButton>
          <IconButton
            color={params.row.isActive ? 'error' : 'success'}
            onClick={() =>
              handleMembershipUpdate(params.row._id, {
                isActive: !params.row.isActive,
              })
            }
          >
            {params.row.isActive ? <Block /> : <CheckCircle />}
          </IconButton>
        </Box>
      ),
    },
  ];

  const premiumUsersCount = memberships.filter((m) => {
    const planName = (m.membershipType || '').toLowerCase();
    return planName && planName !== 'free';
  }).length;

  const activeSubscriptionsCount = memberships.filter((m) => {
    const status = (m.membershipStatus || '').toLowerCase();
    return status === 'active';
  }).length;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Membership Management
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Monitor plan usage and quickly manage active subscription states.
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Premium Users
              </Typography>
              <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
                {premiumUsersCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Subscriptions
              </Typography>
              <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700 }}>
                {activeSubscriptionsCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Expired Plans
              </Typography>
              <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 700 }}>
                {expiredPlansCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Expiring in 30 Days
              </Typography>
              <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 700 }}>
                {expiringSoonCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Memberships Data Grid */}
      <Paper sx={{ height: 600, width: '100%', borderRadius: 3, p: 1 }}>
        <DataGrid
          rows={memberships}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          checkboxSelection
          disableRowSelectionOnClick
          loading={loading}
          getRowId={(row) => row._id}
          localeText={{
            noRowsLabel: 'No users with active memberships found',
          }}
        />
      </Paper>

      {/* Edit Membership Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Membership</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="User Name"
                value={selectedUser.name}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Current Membership"
                value={selectedUser.membershipType}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Times Purchased"
                value={selectedUser.purchase_count || 0}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                defaultValue={
                  selectedUser.membershipExpiry
                    ? new Date(selectedUser.membershipExpiry)
                        .toISOString()
                        .split('T')[0]
                    : ''
                }
                onChange={(e) =>
                  handleMembershipUpdate(selectedUser._id, {
                    membershipExpiry: e.target.value,
                  })
                }
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              handleMembershipUpdate(selectedUser._id, {
                membershipType: 'Premium',
                isActive: true,
              })
            }
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembershipManagement; 